import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast, Toaster } from "sonner";
import { Check, ShieldCheck, Heart, Sparkles, ChevronRight } from "lucide-react";
import { PageHeader, PageFooter } from "@/components/SiteChrome";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Wire to Square Online Booking URL when ready
const BOOKING_URL = "#book";

const SESSIONS = [
  { id: "mini", label: "The Mini Reset", duration: "30 min", price: "$55" },
  { id: "sacred-crown", label: "The Sacred Crown Ritual", duration: "60 min", price: "$111" },
  { id: "soft-sanctuary", label: "The Soft Sanctuary", duration: "90 min", price: "$177" },
  { id: "divine-feminine", label: "The Divine Feminine Ritual", duration: "120 min", price: "$222" },
  { id: "not-sure", label: "Not sure yet — guide me", duration: "—", price: "" },
];

const fieldClass =
  "w-full rounded-sm border border-[#eae0d5] bg-[#fdfbf7] px-4 py-3 text-[15px] font-light text-[#2d2926] placeholder:text-[#a8a098] focus:outline-none focus:border-[#b89461] transition-colors";

const labelClass =
  "block text-[11px] tracking-[0.28em] uppercase text-[#7a5e36] mb-2 font-medium";

export default function Intake() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    preferred_session: "sacred-crown",
    previous_experience: "no",
    health_notes: "",
    allergies: "",
    intention: "",
    tea_or_cacao: "tea",
    referral_source: "",
    consent: false,
  });

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Please confirm the women-only, non-intimate consent before continuing.");
      return;
    }
    if (!form.full_name.trim() || !form.email.trim()) {
      toast.error("Name and email are required.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/intake`, form);
      setSubmitted(data);
      toast.success("Intake received. Welcome to Sacred Crown.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#fdfbf7] text-[#2d2926] font-body">
        <Toaster richColors position="top-center" />
        <PageHeader />
        <main data-testid="intake-success" className="mx-auto max-w-2xl px-6 sm:px-10 py-20 md:py-28 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7 }}
          >
            <div className="mx-auto w-14 h-14 rounded-full bg-[#b89461]/15 flex items-center justify-center">
              <Check className="text-[#b89461]" strokeWidth={1.4} />
            </div>
            <p className="mt-8 font-display text-[11px] tracking-[0.45em] text-[#b89461]">
              YOU&rsquo;RE IN GOOD HANDS
            </p>
            <h1 className="mt-4 font-display text-3xl sm:text-4xl uppercase tracking-tight leading-[1.15] text-[#2d2926]">
              Thank you, <span className="italic text-[#7a5e36] normal-case">{submitted.full_name.split(" ")[0]}.</span>
            </h1>
            <p className="mt-6 text-[15px] leading-[1.85] text-[#3f3a35] font-light">
              Your intake is safely received. The final step is choosing your time and
              completing payment through our secure booking partner. Your details are
              already noted — Arlene will see them before your session.
            </p>

            <a
              href={BOOKING_URL}
              data-testid="intake-continue-booking"
              className="mt-10 inline-flex items-center justify-center gap-3 rounded-sm bg-[#b89461] px-9 py-4 text-[11px] font-medium tracking-[0.28em] uppercase text-[#fdfbf7] transition-all duration-500 hover:-translate-y-0.5 hover:bg-[#a07e4f]"
            >
              Continue to Secure Booking <ChevronRight size={16} />
            </a>
            <p className="mt-4 text-[11px] tracking-[0.18em] uppercase text-[#a8a098]">
              Payment processed securely via Square
            </p>

            <button
              onClick={() => navigate("/")}
              className="mt-10 text-[12px] tracking-[0.2em] uppercase text-[#7a5e36] hover:text-[#b89461]"
            >
              ← Back to home
            </button>
          </motion.div>
        </main>
        <PageFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fdfbf7] text-[#2d2926] font-body">
      <Toaster richColors position="top-center" />
      <PageHeader />

      <main className="mx-auto max-w-3xl px-6 sm:px-10 py-16 md:py-20" data-testid="intake-page">
        {/* Hero */}
        <div className="text-center">
          <p className="font-display text-[11px] tracking-[0.45em] text-[#b89461]">CLIENT INTAKE</p>
          <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tight leading-[1.1] text-[#2d2926]">
            Before You Arrive,
            <span className="block text-[#7a5e36]">A Few Soft Questions.</span>
          </h1>
          <p className="mt-6 max-w-xl mx-auto text-[15px] leading-[1.85] text-[#3f3a35] font-light">
            Your answers help your session feel made for you — and let Arlene prepare
            the room, the scents, and the tea before you walk in.
          </p>
        </div>

        {/* Trust strip */}
        <div className="mt-12 grid grid-cols-3 gap-4 max-w-xl mx-auto text-center">
          <div className="flex flex-col items-center gap-2">
            <ShieldCheck className="text-[#b89461]" size={22} strokeWidth={1.2} />
            <p className="text-[11px] tracking-[0.15em] text-[#5c544f]">Private & Confidential</p>
          </div>
          <div className="flex flex-col items-center gap-2">
            <Heart className="text-[#b89461]" size={22} strokeWidth={1.2} />
            <p className="text-[11px] tracking-[0.15em] text-[#5c544f]">Women Only</p>
          </div>
          <div className="flex flex-col items-center gap-2">
            <Sparkles className="text-[#b89461]" size={22} strokeWidth={1.2} />
            <p className="text-[11px] tracking-[0.15em] text-[#5c544f]">3 Minutes to Complete</p>
          </div>
        </div>

        <form onSubmit={onSubmit} className="mt-14 space-y-10" data-testid="intake-form">
          {/* Identity */}
          <section className="space-y-6">
            <h2 className="font-display text-xl uppercase tracking-[0.12em] text-[#2d2926]">
              About You
            </h2>
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label className={labelClass}>Full Name *</label>
                <input
                  data-testid="intake-name"
                  className={fieldClass}
                  value={form.full_name}
                  onChange={set("full_name")}
                  required
                />
              </div>
              <div>
                <label className={labelClass}>Email *</label>
                <input
                  type="email"
                  data-testid="intake-email"
                  className={fieldClass}
                  value={form.email}
                  onChange={set("email")}
                  required
                />
              </div>
              <div>
                <label className={labelClass}>Phone</label>
                <input
                  type="tel"
                  data-testid="intake-phone"
                  className={fieldClass}
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="Optional"
                />
              </div>
              <div>
                <label className={labelClass}>How did you hear about us?</label>
                <input
                  data-testid="intake-referral"
                  className={fieldClass}
                  value={form.referral_source}
                  onChange={set("referral_source")}
                  placeholder="Instagram, friend, search…"
                />
              </div>
            </div>
          </section>

          {/* Session */}
          <section className="space-y-6">
            <h2 className="font-display text-xl uppercase tracking-[0.12em] text-[#2d2926]">
              Your Session
            </h2>
            <div>
              <label className={labelClass}>Which ritual feels right? *</label>
              <div className="grid gap-3 sm:grid-cols-2">
                {SESSIONS.map((s) => {
                  const active = form.preferred_session === s.id;
                  return (
                    <label
                      key={s.id}
                      data-testid={`intake-session-${s.id}`}
                      className={`cursor-pointer rounded-sm border px-4 py-4 transition-all ${
                        active
                          ? "border-[#b89461] bg-[#b89461]/8"
                          : "border-[#eae0d5] hover:border-[#d0b49f]"
                      }`}
                    >
                      <input
                        type="radio"
                        name="session"
                        className="sr-only"
                        checked={active}
                        onChange={() => setForm((f) => ({ ...f, preferred_session: s.id }))}
                      />
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="font-display text-[15px] text-[#2d2926]">{s.label}</p>
                          {s.duration !== "—" && (
                            <p className="text-[11px] tracking-[0.2em] uppercase text-[#7a5e36] mt-1">
                              {s.duration}
                            </p>
                          )}
                        </div>
                        {s.price && (
                          <p className="font-display text-lg text-[#7a5e36]">{s.price}</p>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label className={labelClass}>Tea or Cacao?</label>
                <select
                  data-testid="intake-tea-cacao"
                  className={fieldClass + " pr-10"}
                  value={form.tea_or_cacao}
                  onChange={set("tea_or_cacao")}
                >
                  <option value="tea">Herbal Tea</option>
                  <option value="cacao">Ceremonial Cacao</option>
                  <option value="either">Either is lovely</option>
                </select>
              </div>
              <div>
                <label className={labelClass}>Have you experienced something like this before?</label>
                <select
                  data-testid="intake-previous"
                  className={fieldClass + " pr-10"}
                  value={form.previous_experience}
                  onChange={set("previous_experience")}
                >
                  <option value="no">No, this is new for me</option>
                  <option value="yes">Yes, similar work before</option>
                </select>
              </div>
            </div>
          </section>

          {/* Body & comfort */}
          <section className="space-y-6">
            <h2 className="font-display text-xl uppercase tracking-[0.12em] text-[#2d2926]">
              So I Can Care For You Well
            </h2>
            <div>
              <label className={labelClass}>What brought you to Sacred Crown today?</label>
              <textarea
                rows={3}
                data-testid="intake-intention"
                className={fieldClass + " resize-none"}
                value={form.intention}
                onChange={set("intention")}
                placeholder="A word, a feeling, a season of life — anything you want me to know."
              />
            </div>
            <div>
              <label className={labelClass}>Anything about your nervous system or health I should know?</label>
              <textarea
                rows={3}
                data-testid="intake-health"
                className={fieldClass + " resize-none"}
                value={form.health_notes}
                onChange={set("health_notes")}
                placeholder="Recent stress, pregnancy, migraines, scalp sensitivity, sound sensitivity — anything."
              />
            </div>
            <div>
              <label className={labelClass}>Scent or product allergies?</label>
              <textarea
                rows={2}
                data-testid="intake-allergies"
                className={fieldClass + " resize-none"}
                value={form.allergies}
                onChange={set("allergies")}
                placeholder="Lavender, citrus, essential oils, etc."
              />
            </div>
          </section>

          {/* Consent */}
          <section className="rounded-sm border border-[#eae0d5] bg-[#f6f1ea]/60 p-6">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                data-testid="intake-consent"
                checked={form.consent}
                onChange={set("consent")}
                className="mt-1 h-4 w-4 accent-[#b89461]"
              />
              <span className="text-[13px] leading-[1.7] text-[#3f3a35] font-light">
                I understand that Sacred Crown Studio is a women-only, professional
                relaxation experience. The session is strictly non-sexual and
                non-clinical, focused on gentle hair brushing, scalp relaxation,
                aromatherapy, and sound healing. I&rsquo;m arriving in good faith and
                ready to receive.
              </span>
            </label>
          </section>

          <div className="flex flex-col items-center gap-4">
            <button
              type="submit"
              disabled={submitting}
              data-testid="intake-submit"
              className="inline-flex items-center justify-center gap-3 rounded-sm bg-[#b89461] px-12 py-4 text-[11px] font-medium tracking-[0.28em] uppercase text-[#fdfbf7] transition-all duration-500 hover:-translate-y-0.5 hover:bg-[#a07e4f] disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? "Sending…" : "Continue To Booking"}
              {!submitting && <ChevronRight size={16} />}
            </button>
            <p className="text-[11px] tracking-[0.2em] uppercase text-[#a8a098]">
              Next step: choose a time & pay securely via Square
            </p>
            <Link to="/" className="text-[12px] tracking-[0.2em] uppercase text-[#7a5e36] hover:text-[#b89461]">
              ← Back to home
            </Link>
          </div>
        </form>
      </main>

      <PageFooter />
    </div>
  );
}
